import { Fragment, type CSSProperties } from 'react';
import { useTheme } from 'styled-components';
import { format, highlight } from '../../utils/number';
import { BPS_PRICE_DISPLAY_SPEC, BPS_PRICE_INCREMENT } from '../../utils/pricingMode';
import { Currency, StackedNumber } from './styles';
import type { NumberVariants } from './types';

export function StackedFormattedNumber({
  number,
  increment: providedIncrement,
  specification: providedSpecification,
  highlightAll,
  variant,
  round = false,
  pretty = true,
  largeSize,
  baseSize,
  currency,
  showBPSLabel = false,
}: StackedFormattedNumberProps) {
  // For BPS mode, always show 2 decimals and override specification to keep decimals visible.
  // We override because the security's increment/specification assumes cash pricing, not BPS.
  const increment = showBPSLabel ? BPS_PRICE_INCREMENT : providedIncrement;
  const specification = showBPSLabel ? BPS_PRICE_DISPLAY_SPEC : (providedSpecification ?? 'M.m');

  const numberString = number == null ? '-' : format(number, { spec: increment, round, pretty });
  const hi = highlight({ number: numberString, specification });
  const value = hi.value;
  const startHighlight = hi.highlight[0];
  const endHighlight = hi.highlight[1];
  const theme = useTheme();

  // In BPS mode, if decimals are kept in the middle stack (specification like 'M.MM'),
  // the bottom stack will be empty, so we can show the BPS label there
  const hasDecimalsInBottom = endHighlight != null && endHighlight + 1 < value.length;
  const shouldShowBPSLabel = showBPSLabel && !hasDecimalsInBottom && number != null;
  const remainingDecimalPart = hasDecimalsInBottom && endHighlight != null ? value.slice(endHighlight + 1) : null;

  const parts: JSX.Element[] = [];
  if (startHighlight == null || endHighlight == null) {
    return (
      <>
        <StackedNumber
          key={1}
          stack="top"
          large={false}
          variant={variant}
          highlight={!!highlightAll}
          largeSize={largeSize}
          baseSize={baseSize}
        >
          &nbsp;
        </StackedNumber>
        <StackedNumber
          key={2}
          stack="middle"
          large={true}
          variant={variant}
          highlight={true}
          largeSize={largeSize}
          baseSize={baseSize}
        >
          {value}
          {currency && (
            <Currency isSmall={false} variant={variant} theme={theme} data-testid="full-number-currency">
              {' '}
              {currency}
            </Currency>
          )}
        </StackedNumber>
        <StackedNumber
          key={3}
          stack="bottom"
          large={false}
          variant={variant}
          highlight={!!highlightAll}
          largeSize={largeSize}
          baseSize={baseSize}
        >
          {shouldShowBPSLabel ? 'BPS' : <>&nbsp;</>}
        </StackedNumber>
      </>
    );
  } else {
    parts.push(
      <StackedNumber
        key={1}
        stack="top"
        large={false}
        variant={variant}
        highlight={!!highlightAll}
        largeSize={largeSize}
        baseSize={baseSize}
      >
        {startHighlight > 0 ? value.slice(0, startHighlight) : <>&nbsp;</>}
      </StackedNumber>
    );
    parts.push(
      <StackedNumber key={2} large={true} variant={variant} highlight={true} largeSize={largeSize} baseSize={baseSize}>
        {value.slice(startHighlight, endHighlight + 1)}
        {currency && (
          <Currency isSmall={false} variant={variant} theme={theme} data-testid="full-number-currency">
            {' '}
            {currency}
          </Currency>
        )}
      </StackedNumber>
    );
    parts.push(
      <StackedNumber
        key={3}
        stack="bottom"
        large={false}
        variant={variant}
        highlight={!!highlightAll}
        largeSize={largeSize}
        baseSize={baseSize}
      >
        {shouldShowBPSLabel ? 'BPS' : (remainingDecimalPart ?? <>&nbsp;</>)}
      </StackedNumber>
    );
    return <Fragment>{parts}</Fragment>;
  }
}

type StackedFormattedNumberProps = {
  number?: string | number;
  specification?: string;
  highlightAll?: boolean;
  variant?: NumberVariants;
  round?: boolean;
  pretty?: boolean;
  increment?: string;
  largeSize?: CSSProperties['fontSize'];
  baseSize?: CSSProperties['fontSize'];
  currency?: string | null;
  showBPSLabel?: boolean;
};
